import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import * as companyApi from '../../services/company-api.js';
import type { CompanyBudget, BudgetUsageEntry, CompanyAgent } from '../../services/company-api.js';

@customElement('cp-company-budget-widget')
export class CpCompanyBudgetWidget extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) companyId = 0;
  @property({ type: Array }) agents: CompanyAgent[] = [];

  @state() private _budget: CompanyBudget | null = null;
  @state() private _usageByAgent: BudgetUsageEntry[] = [];
  @state() private _loading = false;
  @state() private _saving = false;
  @state() private _error = '';
  @state() private _editLimit = '';
  @state() private _showEdit = false;

  override async connectedCallback(): Promise<void> {
    super.connectedCallback();
    await this._load();
  }

  private async _load(): Promise<void> {
    if (!this.companyId) return;
    this._loading = true;
    this._error = '';
    try {
      const data = await companyApi.getCompanyBudget(this.companyId);
      this._budget = data.budget;
      this._usageByAgent = data.usageByAgent;
      if (this._budget) {
        this._editLimit = String(this._budget.monthly_limit);
      }
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden';
    } finally {
      this._loading = false;
    }
  }

  private async _saveBudget(): Promise<void> {
    const limit = parseInt(this._editLimit, 10);
    if (isNaN(limit) || limit < 0) {
      this._error = 'Bitte eine gueltige Zahl eingeben (>= 0)';
      return;
    }
    this._saving = true;
    this._error = '';
    try {
      this._budget = await companyApi.setCompanyBudget(this.companyId, limit);
      this._showEdit = false;
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Speichern';
    } finally {
      this._saving = false;
    }
  }

  private async _resetBudget(): Promise<void> {
    const limit = this._showEdit && this._editLimit
      ? parseInt(this._editLimit, 10)
      : undefined;
    this._saving = true;
    this._error = '';
    try {
      this._budget = await companyApi.resetCompanyBudget(this.companyId, limit !== undefined && !isNaN(limit) ? limit : undefined);
      this._usageByAgent = [];
      this._showEdit = false;
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Zuruecksetzen';
    } finally {
      this._saving = false;
    }
  }

  private _agentName(agentId: number): string {
    return this.agents.find((a) => a.id === agentId)?.name ?? `Agent #${agentId}`;
  }

  private _progressPercent(): number {
    if (!this._budget || this._budget.monthly_limit === 0) return 0;
    return Math.min(100, Math.round((this._budget.current_usage / this._budget.monthly_limit) * 100));
  }

  private _isExhausted(): boolean {
    if (!this._budget || this._budget.monthly_limit === 0) return false;
    return this._budget.current_usage >= this._budget.monthly_limit;
  }

  override render() {
    if (this._loading) {
      return html`<p class="cp-budget__loading">Laden...</p>`;
    }

    const budget = this._budget;
    const exhausted = this._isExhausted();
    const pct = this._progressPercent();
    const unlimited = !budget || budget.monthly_limit === 0;

    return html`
      <div style="display:flex;flex-direction:column;gap:var(--spacing-md);">
        ${this._error ? html`
          <p class="cp-budget__error">${this._error}</p>
        ` : nothing}

        ${exhausted ? html`
          <div class="cp-budget__exhausted-banner">
            <div class="cp-budget__exhausted-info">
              <span class="cp-budget__exhausted-dot"></span>
              <p class="cp-budget__exhausted-text">Budget erschoepft — Agents pausiert</p>
            </div>
            <button
              class="cp-budget__exhausted-btn"
              @click=${() => { this._showEdit = true; }}
              ?disabled=${this._saving}
            >
              Budget erhoehen
            </button>
          </div>
        ` : nothing}

        <!-- Usage Bar -->
        <div style="display:flex;flex-direction:column;gap:var(--spacing-sm);">
          <div class="cp-budget__usage-header">
            <span class="cp-budget__usage-label">API-Calls diesen Monat</span>
            ${unlimited ? html`
              <span class="cp-budget__usage-unlimited">Unbegrenzt</span>
            ` : html`
              <span class="${exhausted ? 'cp-budget__usage-value--exhausted' : 'cp-budget__usage-value'}">
                ${budget?.current_usage ?? 0} / ${budget?.monthly_limit}
              </span>
            `}
          </div>

          ${!unlimited ? html`
            <div class="cp-budget__progress-track">
              <div
                class="cp-budget__progress-bar ${exhausted ? 'cp-budget__progress-bar--exhausted' : pct > 80 ? 'cp-budget__progress-bar--warn' : 'cp-budget__progress-bar--ok'}"
                style="width: ${pct}%"
              ></div>
            </div>
          ` : nothing}
        </div>

        <!-- Per-Agent Breakdown -->
        ${this._usageByAgent.length > 0 ? html`
          <div style="display:flex;flex-direction:column;gap:var(--spacing-xs);">
            <p class="cp-budget__breakdown-label">Verbrauch pro Agent</p>
            <div class="cp-budget__breakdown-list">
              ${this._usageByAgent.map((u) => html`
                <div class="cp-budget__breakdown-row">
                  <span class="cp-budget__breakdown-agent">${this._agentName(u.agent_id)}</span>
                  <span class="cp-budget__breakdown-calls">${u.total_calls} Calls</span>
                </div>
              `)}
            </div>
          </div>
        ` : nothing}

        <!-- Edit / Configure Budget -->
        ${this._showEdit ? html`
          <div style="display:flex;flex-direction:column;gap:var(--spacing-sm);padding-top:var(--spacing-xs);">
            <label class="cp-budget__edit-label">Monatliches Limit (0 = unbegrenzt)</label>
            <div class="cp-budget__edit-row">
              <input
                type="number"
                min="0"
                .value=${this._editLimit}
                @input=${(e: Event) => { this._editLimit = (e.target as HTMLInputElement).value; }}
                class="cp-budget__edit-input"
                placeholder="z.B. 1000"
              />
              <button
                @click=${this._saveBudget}
                ?disabled=${this._saving}
                class="cp-budget__btn-save"
              >
                ${this._saving ? 'Speichert...' : 'Speichern'}
              </button>
              <button
                @click=${() => { this._showEdit = false; this._error = ''; }}
                class="cp-budget__btn-cancel"
              >
                Abbrechen
              </button>
            </div>
          </div>
        ` : html`
          <div class="cp-budget__actions">
            <button
              class="cp-budget__action-link"
              @click=${() => { this._showEdit = true; }}
            >
              ${budget ? 'Budget aendern' : 'Budget konfigurieren'}
            </button>
            ${exhausted ? html`
              <span class="cp-budget__action-sep">·</span>
              <button
                class="cp-budget__action-link cp-budget__action-link--success"
                @click=${this._resetBudget}
                ?disabled=${this._saving}
              >
                ${this._saving ? 'Wird zurueckgesetzt...' : 'Zuruecksetzen & Agents reaktivieren'}
              </button>
            ` : nothing}
          </div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-company-budget-widget': CpCompanyBudgetWidget;
  }
}
